import { donutArcs, type ChartSegment } from './cpc25Charts';

interface Cpc25DonutChartProps {
    title: string;
    segments: ChartSegment[];
    emptyMessage?: string;
}

// Rosca (donut) desenhada com SVG puro (sem lib): cada fatia é um <circle> com stroke e
// dasharray/dashoffset. Ideal para proporção (casos por classificação). Legenda ao lado com
// contagem e %. Dark mode. O total central mostra a soma.
const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const Cpc25DonutChart = ({ title, segments, emptyMessage = 'Sem dados' }: Cpc25DonutChartProps) => {
    const arcs = donutArcs(segments, CIRCUMFERENCE);
    const total = segments.reduce((sum, s) => sum + s.value, 0);

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</h3>
            {arcs.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">{emptyMessage}</p>
            ) : (
                <div className="flex items-center gap-5">
                    <svg viewBox="0 0 100 100" className="w-28 h-28 shrink-0 -rotate-90" role="img"
                        aria-label={`${title}: ${segments.map((s) => `${s.label} ${s.formatted}`).join(', ')}`}>
                        <circle cx="50" cy="50" r={RADIUS} fill="none" strokeWidth="12"
                            className="stroke-gray-100 dark:stroke-slate-700" />
                        {arcs.map((a) => (
                            <circle
                                key={a.key}
                                cx="50" cy="50" r={RADIUS} fill="none" strokeWidth="12"
                                stroke={a.color}
                                strokeDasharray={a.dashArray}
                                strokeDashoffset={a.dashOffset}
                            >
                                <title>{`${a.label}: ${a.formatted} (${a.pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)`}</title>
                            </circle>
                        ))}
                        <text x="50" y="50" transform="rotate(90 50 50)" textAnchor="middle"
                            dominantBaseline="central" className="fill-gray-900 dark:fill-white"
                            style={{ fontSize: '18px', fontWeight: 600 }}>
                            {total}
                        </text>
                    </svg>
                    <div className="flex flex-col gap-1.5">
                        {arcs.map((a) => (
                            <div key={a.key} className="flex items-center gap-1.5 text-xs">
                                <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                                <span className="text-gray-600 dark:text-gray-300">{a.label}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{a.formatted}</span>
                                <span className="text-gray-400 dark:text-gray-500">
                                    ({a.pct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cpc25DonutChart;
